import React, { useCallback, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Button, TextField } from '@mui/material';
import { useMeetingHubContext } from 'components/meetings/MeetingPage/MeetingHubContext';
import { useMeetingStaffContext } from '../../../MeetingStaffContext';
import MeetingService from 'services/MeetingService';
import GridContainer from 'components/shared/GridContainer';
import GridItem from 'components/shared/GridItem';
import { ApprovalLevel } from 'store/types/Meeting';
import { ActivityType } from 'store/enums/BallotReviewTypes';
import { defaultSnackbarErrorProps } from 'util/layout';
import { meetingSessionToActivity } from 'util/meeting';

import commonStyles from 'styles/common.module.scss';

const defaultStatement = `Globally Approved (No Ratification Ballot needed):
	Line Item [X] meets the Letter Ballot approval conditions for the global technical committee.

Need a Ratification Ballot:
	Line Item [X] meets the Letter Ballot approval conditions for the TC Chapter and a Ratification Ballot will be issued to validate technical changes.`;

const ApprovalLevelLineItemSection: React.FunctionComponent = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { meetingId, meetingSession } = useMeetingHubContext();
  const { logActivity } = useMeetingStaffContext();
  const [statement, setStatement] = useState(defaultStatement);

  const currentApprovalLevel: ApprovalLevel = useMemo(() => {
    return {
      statement: statement,
    };
  }, [statement]);

  const handleDisplayApprovalLevel = useCallback(async () => {
    try {
      if (!statement) {
        enqueueSnackbar(`Please enter an approval level statement before displaying.`, { variant: 'warning' });
        return;
      }

      const updatedSession = await MeetingService.saveMeetingSession(
        meetingSession?.currentDocument
          ? {
              ...meetingSession,
              meetingId,
              currentApprovalLevel,
              currentDocument: {
                ...meetingSession?.currentDocument,
                lineItemNumber: undefined,
                lineItemTitle: undefined,
              },
              currentReject: undefined,
              currentTechnicalIssue: undefined,
              currentComment: undefined,
              currentMotion: undefined,
              currentVotingSession: undefined,
            }
          : {
              ...meetingSession,
              meetingId,
              currentApprovalLevel,
              currentReject: undefined,
              currentTechnicalIssue: undefined,
              currentComment: undefined,
              currentMotion: undefined,
              currentVotingSession: undefined,
            }
      );

      logActivity?.({
        ...meetingSessionToActivity(updatedSession),
        type: ActivityType.Approval,
        approvalLevel: updatedSession.currentApprovalLevel,
      });

      enqueueSnackbar(`Displayed approval level`, { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(`Failed to display approval level`, defaultSnackbarErrorProps);
      console.error('Error displaying approval level:', error);
    }
  }, [currentApprovalLevel, enqueueSnackbar, logActivity, meetingId, meetingSession, statement]);

  const handleClearApprovalLevel = useCallback(async () => {
    try {
      await MeetingService.saveMeetingSession({
        ...meetingSession,
        meetingId,
        currentApprovalLevel: undefined,
      });

      enqueueSnackbar(`Cleared approval level`, { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(`Failed to clear approval level`, defaultSnackbarErrorProps);
      console.error('Error clearing approval level:', error);
    }
  }, [enqueueSnackbar, meetingId, meetingSession]);

  return (
    <GridContainer>
      <GridItem>
        <TextField
          label={'Approval Level'}
          value={statement}
          required
          multiline
          onChange={(event) => {
            setStatement(event.target.value);
          }}
        ></TextField>
      </GridItem>
      <GridItem className={commonStyles.buttonGroup}>
        <Button variant="contained" color="secondary" onClick={handleDisplayApprovalLevel}>
          Display Approval Level
        </Button>
        <Button variant="contained" color="error" onClick={handleClearApprovalLevel}>
          Clear Approval Level
        </Button>
      </GridItem>
    </GridContainer>
  );
};

export default ApprovalLevelLineItemSection;
